import type { HomeStageId, OrderView, PaymentIntentResponse, QuoteRequest, QuoteResponse, TaxAddress } from "@townsy/contracts";
import { describeCommerceError, type CommerceFailure } from "./errors";
import { CHECKOUT_POLL_LIMIT_MS, canPayNow, isSettled, orderPollDelayMs } from "./orderStatus";
import type { CardConfirmOutcome } from "./stripeResult";

/**
 * The dollar checkout as a small, dependency-injected state machine (docs/04 sections 6-8,
 * ADR-0007). It never decides success: it asks the server for a quote, creates ONE idempotent order
 * per confirmed quote, collects only the card shortfall through the provider, and then polls
 * `GET /orders/:id` until the server says the order is fulfilled, expired, canceled, refunded or
 * under review. A return URL or a Stripe "succeeded" is only a hint to start polling.
 *
 *   (address ->) quoting -> review -> ordering -> (all credit) settled
 *                                            -> preparing_payment -> payment -> confirming -> processing -> settled
 *   payment -> canceling -> processing/settled          review/payment --(errors)--> failed
 *
 * ADR-0046: with Stripe Tax on (`addressRequired`, from GET /payments/config, or the server answering
 * TAX_ADDRESS_REQUIRED) the buyer gives a country and postal code first; the price, the tax and the card amount are
 * then fixed by the server's quote. The browser never computes or sends a tax amount.
 */

export type QuoteTarget =
  | { kind: "COMMERCIAL_ACQUISITION"; propertyId: string; expectedPropertyVersion: number }
  | { kind: "RESIDENTIAL_PLOT"; propertyId: string }
  | { kind: "HOME_UPGRADE"; propertyId: string; toStage: HomeStageId }
  | { kind: "CATALOG_ITEM"; itemId: string }
  /** M5: whole UTC days on one board with an APPROVED billboard creative (the server prices the term). */
  | { kind: "BILLBOARD_RENTAL"; billboardId: string; startDate: string; termDays: 7 | 30; creativeId: string }
  /** M6: the cents budget of the buyer's own sponsored campaign. CARD ONLY: Townsy Credit never funds a campaign. */
  | { kind: "CAMPAIGN_FUNDING"; campaignId: string; amountCents: number };

/** Targets the card pays in full: no Townsy Credit toggle, no credit in the quote (docs/04 section 2). */
export function isCardOnly(target: Pick<QuoteTarget, "kind"> | null | undefined): boolean {
  return target?.kind === "CAMPAIGN_FUNDING";
}

export type CheckoutStep =
  /** ADR-0046: the buyer's country and postal code for sales tax, before any price is fixed. */
  | "address"
  | "quoting"
  | "review"
  | "ordering"
  | "preparing_payment"
  | "payment"
  | "confirming"
  | "processing"
  | "canceling"
  | "settled"
  | "failed";

export interface CheckoutState {
  step: CheckoutStep;
  target: QuoteTarget | null;
  useCredit: boolean;
  quote: QuoteResponse | null;
  /** The quote's expiry passed on the (server-adjusted) clock: confirming asks for a new price. */
  quoteExpired: boolean;
  order: OrderView | null;
  intent: PaymentIntentResponse | null;
  /** Last API failure, shown inline (blocking only in `failed`). */
  failure: CommerceFailure | null;
  /** Card decline / validation / 3D Secure message from the payment step. */
  cardMessage: string | null;
  /** Polling in this dialog stopped before the order settled (the order page keeps going). */
  pollTimedOut: boolean;
  /** ADR-0046: Stripe Tax is on: quotes need the buyer's location. */
  addressRequired: boolean;
  /** The location the buyer gave (sent with every quote while addressRequired). */
  taxAddress: TaxAddress | null;
}

export interface CheckoutApi {
  quote(body: QuoteRequest): Promise<QuoteResponse>;
  createOrder(quoteId: string, idempotencyKey: string): Promise<{ order: OrderView; paymentIntent: PaymentIntentResponse | null }>;
  paymentIntent(orderId: string): Promise<PaymentIntentResponse>;
  getOrder(orderId: string): Promise<OrderView>;
  cancel(orderId: string): Promise<OrderView>;
}

export interface CheckoutDeps {
  api: CheckoutApi;
  /** Server-adjusted epoch ms (see lib/serverClock). */
  now(): number;
  sleep(ms: number): Promise<void>;
  makeKey(): string;
  onChange(state: CheckoutState): void;
  /** Every fresh order view from the server (the host refreshes caches and toasts on fulfilment). */
  onOrder?(order: OrderView): void;
  /** Limit for polling inside the dialog (default CHECKOUT_POLL_LIMIT_MS). */
  pollLimitMs?: number;
  /**
   * Before a NEW quote (expired, stale, version changed), re-read what the buyer is looking at:
   * a commercial quote must carry the property's current version (PROPERTY_VERSION_CHANGED otherwise).
   * The review then shows the new price and still needs an explicit confirmation.
   */
  refreshTarget?(target: QuoteTarget): Promise<QuoteTarget>;
}

/** After an unclear card result, poll this long before offering the card form again. */
export const CHECK_WINDOW_MS = 8_000;

export const INITIAL_CHECKOUT: CheckoutState = {
  step: "quoting",
  target: null,
  useCredit: true,
  quote: null,
  quoteExpired: false,
  order: null,
  intent: null,
  failure: null,
  cardMessage: null,
  pollTimedOut: false,
  addressRequired: false,
  taxAddress: null,
};

export function quoteBody(target: QuoteTarget, useCredit: boolean, taxAddress: TaxAddress | null = null): QuoteRequest {
  // ADR-0046: only the buyer's location is sent for sales tax; the server computes the tax.
  const tax = taxAddress ? { taxAddress } : {};
  switch (target.kind) {
    case "COMMERCIAL_ACQUISITION":
      return { kind: target.kind, propertyId: target.propertyId, expectedPropertyVersion: target.expectedPropertyVersion, useCredit, ...tax };
    case "RESIDENTIAL_PLOT":
      return { kind: target.kind, propertyId: target.propertyId, useCredit, ...tax };
    case "HOME_UPGRADE":
      return { kind: target.kind, propertyId: target.propertyId, toStage: target.toStage, useCredit, ...tax };
    case "CATALOG_ITEM":
      return { kind: target.kind, itemId: target.itemId, useCredit, ...tax };
    case "BILLBOARD_RENTAL":
      return { kind: target.kind, billboardId: target.billboardId, startDate: target.startDate, termDays: target.termDays, creativeId: target.creativeId, useCredit, ...tax };
    case "CAMPAIGN_FUNDING":
      // Card only, whatever the toggle says: the request can never ask for Townsy Credit.
      return { kind: target.kind, campaignId: target.campaignId, amountCents: target.amountCents, useCredit: false, ...tax };
  }
}

export type CheckoutInit =
  | {
      target: QuoteTarget;
      useCredit?: boolean;
      /** ADR-0046: the server's payments config says Stripe Tax is on (ask for the location before quoting). */
      addressRequired?: boolean;
      /** A location the buyer already gave in this browser session. */
      taxAddress?: TaxAddress | null;
    }
  | { orderId: string };

export class CheckoutController {
  private s: CheckoutState;
  private readonly initial: CheckoutInit;
  private disposed = false;
  /** Bumped by every user action; async results of an older action are dropped. */
  private op = 0;
  /** Bumped whenever polling must stop or restart. */
  private pollGen = 0;
  /** One Idempotency-Key per quote: retries of the same confirmation reuse it verbatim. */
  private readonly keys = new Map<string, string>();

  constructor(
    private readonly deps: CheckoutDeps,
    init: CheckoutInit,
  ) {
    this.initial = init;
    const target = "target" in init ? init.target : null;
    this.s = {
      ...INITIAL_CHECKOUT,
      target,
      useCredit: target ? !isCardOnly(target) && (("useCredit" in init ? init.useCredit : undefined) ?? true) : true,
      addressRequired: "target" in init ? init.addressRequired === true : false,
      taxAddress: "target" in init ? (init.taxAddress ?? null) : null,
    };
  }

  get state(): CheckoutState {
    return this.s;
  }

  /** Begin: ask for a quote, or (resume) read the existing order and continue from its state. */
  start(): Promise<void> {
    if ("orderId" in this.initial) return this.resume(this.initial.orderId);
    return this.requestQuote();
  }

  dispose(): void {
    this.disposed = true;
    this.pollGen++;
  }

  private set(patch: Partial<CheckoutState>): void {
    if (this.disposed) return;
    this.s = { ...this.s, ...patch };
    this.deps.onChange(this.s);
  }

  private begin(): number {
    this.pollGen++;
    return ++this.op;
  }

  private live(token: number): boolean {
    return !this.disposed && token === this.op;
  }

  private seeOrder(order: OrderView): void {
    this.deps.onOrder?.(order);
  }

  // ------------------------------------------------------------------ quote

  /** A NEW price after an expired/stale quote or a failure: refresh the target, then quote again. */
  requote(): Promise<void> {
    return this.requestQuote({ refresh: true });
  }

  async requestQuote(opts: { refresh?: boolean } = {}): Promise<void> {
    let target = this.s.target;
    if (!target) return;
    if (this.s.addressRequired && !this.s.taxAddress) {
      // ADR-0046: no price is asked for before the buyer says where they are (sales tax depends on it).
      this.begin();
      this.set({ step: "address", quote: null, quoteExpired: false });
      return;
    }
    const token = this.begin();
    this.set({ step: "quoting", failure: null, quoteExpired: false, cardMessage: null });
    try {
      if (opts.refresh && this.deps.refreshTarget) {
        target = await this.deps.refreshTarget(target);
        if (!this.live(token)) return;
        this.set({ target });
      }
      const quote = await this.deps.api.quote(quoteBody(target, this.s.useCredit, this.s.addressRequired ? this.s.taxAddress : null));
      if (!this.live(token)) return;
      this.set({ step: "review", quote, quoteExpired: this.deps.now() >= Date.parse(quote.expiresAt), failure: null });
    } catch (err) {
      if (!this.live(token)) return;
      const failure = describeCommerceError(err, { stage: "quote", kind: target.kind });
      if (failure.code === "PROPERTY_VERSION_CHANGED" && !opts.refresh && this.deps.refreshTarget) {
        // The panel showed an older version: re-read it once and price the current one (still needs a confirm).
        return this.requestQuote({ refresh: true });
      }
      if (failure.action === "address") {
        // The server needs (a corrected) location: back to the address step with its explanation.
        this.set({ step: "address", failure, quote: null, addressRequired: true });
        return;
      }
      this.set({ step: "failed", failure, quote: null });
    }
  }

  /** ADR-0046: the buyer gave their country and postal code: price with sales tax. */
  submitTaxAddress(address: TaxAddress): Promise<void> {
    if (!(this.s.step === "address" || this.s.step === "review" || this.s.step === "failed")) return Promise.resolve();
    const country = address.country.trim().toUpperCase();
    const postal = (address.postalCode ?? "").trim();
    this.s = { ...this.s, addressRequired: true, taxAddress: { country, postalCode: postal.length ? postal : null }, failure: null };
    return this.requestQuote();
  }

  /** ADR-0046: change the location from the review (a new quote follows the new address). */
  editTaxAddress(): void {
    if (!(this.s.step === "review" || this.s.step === "failed")) return;
    this.begin();
    this.set({ step: "address", failure: null, quote: null, quoteExpired: false });
  }

  /** Toggle "apply my Townsy Credit first": always a new server quote (the client never re-prices). */
  setUseCredit(useCredit: boolean): Promise<void> {
    if (isCardOnly(this.s.target)) return Promise.resolve();
    if (useCredit === this.s.useCredit || !(this.s.step === "review" || this.s.step === "failed")) return Promise.resolve();
    this.s = { ...this.s, useCredit };
    return this.requestQuote();
  }

  /** The review's countdown reached the quote expiry. */
  markQuoteExpired(): void {
    if (this.s.step === "review" && !this.s.quoteExpired) this.set({ quoteExpired: true });
  }

  // ------------------------------------------------------------------ order

  /** The explicit confirm action of the review (or "Try again" after a retryable failure). */
  async confirm(): Promise<void> {
    const quote = this.s.quote;
    if (!quote || !(this.s.step === "review")) return;
    if (this.s.quoteExpired || this.deps.now() >= Date.parse(quote.expiresAt)) {
      // An expired quote is never sent: get a new price first (a new quote gets a new key).
      return this.requote();
    }
    let key = this.keys.get(quote.quoteId);
    if (!key) {
      key = this.deps.makeKey();
      this.keys.set(quote.quoteId, key);
    }
    const token = this.begin();
    this.set({ step: "ordering", failure: null });
    try {
      const res = await this.deps.api.createOrder(quote.quoteId, key);
      if (!this.live(token)) return;
      this.seeOrder(res.order);
      this.set({ order: res.order });
      await this.route(res.order, res.paymentIntent, token);
    } catch (err) {
      if (!this.live(token)) return;
      const failure = describeCommerceError(err, { stage: "order", kind: quote.kind });
      if (failure.action === "retry") {
        // Same quote, same key: resending can only return the same order.
        this.set({ step: "review", failure });
      } else if (failure.action === "requote") {
        this.keys.delete(quote.quoteId);
        this.set({ step: "review", failure, quoteExpired: true });
      } else {
        this.keys.delete(quote.quoteId);
        this.set({ step: "failed", failure });
      }
    }
  }

  /** Continue from whatever state the server reports for this order. */
  private async route(order: OrderView, inlineIntent: PaymentIntentResponse | null, token: number): Promise<void> {
    if (!this.live(token)) return;
    if (isSettled(order.status)) {
      this.set({ step: "settled", order, intent: null });
      return;
    }
    if (canPayNow(order, this.deps.now())) {
      if (inlineIntent && inlineIntent.orderId === order.orderId) {
        this.set({ step: "payment", order, intent: inlineIntent, cardMessage: null });
        return;
      }
      await this.preparePayment(token);
      return;
    }
    // Paid, all-credit still finishing, refund moving, or a hold that has just ended: the server decides.
    this.set({ order });
    void this.poll(token);
  }

  /** Resume an existing order (order page "Continue to payment", or a reopened dialog). */
  async resume(orderId: string): Promise<void> {
    const token = this.begin();
    this.set({ step: "processing", failure: null, pollTimedOut: false });
    try {
      const order = await this.deps.api.getOrder(orderId);
      if (!this.live(token)) return;
      this.seeOrder(order);
      this.set({ order });
      await this.route(order, null, token);
    } catch (err) {
      if (!this.live(token)) return;
      this.set({ step: "failed", failure: describeCommerceError(err, { stage: "order_read" }) });
    }
  }

  // ------------------------------------------------------------------ payment

  async preparePayment(token: number = this.begin()): Promise<void> {
    const order = this.s.order;
    if (!order || !this.live(token)) return;
    this.set({ step: "preparing_payment", failure: null });
    try {
      const intent = await this.deps.api.paymentIntent(order.orderId);
      if (!this.live(token)) return;
      this.set({ step: "payment", intent, cardMessage: null });
    } catch (err) {
      if (!this.live(token)) return;
      const failure = describeCommerceError(err, { stage: "payment_intent", kind: order.kind });
      if (failure.action === "refresh_order") {
        this.set({ failure });
        await this.refreshOrder(token);
        return;
      }
      // Retryable: the dialog offers "Try again" (create-or-reuse is idempotent per order).
      this.set({ step: "preparing_payment", failure });
    }
  }

  /** Retry the payment-intent call after a failure. */
  retryPreparePayment(): Promise<void> {
    return this.preparePayment(this.begin());
  }

  /** The buyer pressed Pay: Stripe.js confirmation (and any 3D Secure window) is in flight. */
  beginConfirm(): boolean {
    if (this.s.step !== "payment") return false;
    this.begin();
    this.set({ step: "confirming", cardMessage: null, failure: null });
    return true;
  }

  /** Stripe.js answered. Never treated as success: a submitted payment only starts polling. */
  async finishConfirm(outcome: CardConfirmOutcome): Promise<void> {
    if (this.s.step !== "confirming") return;
    const token = this.begin();
    if (outcome.kind === "submitted") {
      this.set({ step: "processing", pollTimedOut: false });
      await this.poll(token);
      return;
    }
    if (outcome.kind === "retry_card") {
      this.set({ step: "payment", cardMessage: outcome.message });
      return;
    }
    // Unclear: look at the server's state for a short while before offering the form again.
    this.set({ step: "processing", pollTimedOut: false, cardMessage: outcome.message });
    await this.poll(token, { checkUntil: this.deps.now() + CHECK_WINDOW_MS, message: outcome.message });
  }

  /** The reservation countdown reached zero: ask the server (it may have been paid at the last second). */
  reservationEnded(): Promise<void> {
    if (!(this.s.step === "payment" || this.s.step === "preparing_payment")) return Promise.resolve();
    const token = this.begin();
    this.set({ step: "processing", pollTimedOut: false });
    return this.poll(token);
  }

  // ------------------------------------------------------------------ cancel

  /** A cancellation REQUEST: the provider's actual state decides (a lost race completes the purchase). */
  async cancel(): Promise<void> {
    const order = this.s.order;
    if (!order || !(this.s.step === "payment" || this.s.step === "preparing_payment" || this.s.step === "failed")) return;
    const back = this.s.step === "failed" ? "failed" : this.s.step;
    const token = this.begin();
    this.set({ step: "canceling", failure: null });
    try {
      const after = await this.deps.api.cancel(order.orderId);
      if (!this.live(token)) return;
      this.seeOrder(after);
      this.set({ order: after });
      if (isSettled(after.status)) {
        this.set({ step: "settled" });
        return;
      }
      this.set({ step: "processing", pollTimedOut: false });
      await this.poll(token);
    } catch (err) {
      if (!this.live(token)) return;
      const failure = describeCommerceError(err, { stage: "cancel", kind: order.kind });
      if (failure.action === "refresh_order") {
        this.set({ failure });
        await this.refreshOrder(token);
        return;
      }
      this.set({ step: back, failure });
    }
  }

  // ------------------------------------------------------------------ server state

  /** Re-read the order once and continue from it. */
  async refreshOrder(token: number = this.begin()): Promise<void> {
    const order = this.s.order;
    if (!order) return;
    try {
      const fresh = await this.deps.api.getOrder(order.orderId);
      if (!this.live(token)) return;
      this.seeOrder(fresh);
      this.set({ order: fresh });
      await this.route(fresh, null, token);
    } catch (err) {
      if (!this.live(token)) return;
      this.set({ step: "processing", failure: describeCommerceError(err, { stage: "order_read" }) });
      await this.poll(token);
    }
  }

  /** "Check again" after the in-dialog polling limit. */
  checkAgain(): Promise<void> {
    if (!this.s.order) return Promise.resolve();
    const token = this.begin();
    this.set({ step: "processing", pollTimedOut: false, failure: null });
    return this.poll(token);
  }

  /**
   * Poll `GET /orders/:id` until the order settles. With `checkUntil` (an unclear card result), the
   * card form comes back when the order is still plainly unpaid after the window.
   */
  private async poll(token: number, opts: { checkUntil?: number; message?: string | null } = {}): Promise<void> {
    const order = this.s.order;
    if (!order || !this.live(token)) return;
    const gen = ++this.pollGen;
    const startedAt = this.deps.now();
    const limit = this.deps.pollLimitMs ?? CHECKOUT_POLL_LIMIT_MS;
    let attempt = 0;
    let failures = 0;
    let current = order;
    if (this.s.step !== "processing" && this.s.step !== "settled") this.set({ step: "processing" });
    for (;;) {
      const delay = orderPollDelayMs(current.status, attempt);
      if (delay === false) {
        this.set({ step: "settled", order: current, intent: null });
        return;
      }
      await this.deps.sleep(failures > 0 ? Math.min(10_000, delay * (failures + 1)) : delay);
      if (!this.live(token) || gen !== this.pollGen) return;
      attempt++;
      try {
        current = await this.deps.api.getOrder(order.orderId);
        if (!this.live(token) || gen !== this.pollGen) return;
        failures = 0;
        this.seeOrder(current);
        this.set({ order: current, failure: null });
      } catch (err) {
        if (!this.live(token) || gen !== this.pollGen) return;
        failures++;
        const failure = describeCommerceError(err, { stage: "order_read" });
        this.set({ failure });
        if (failure.action === "close") {
          // Signed out / not found: polling cannot help.
          this.set({ step: "failed" });
          return;
        }
      }
      if (isSettled(current.status)) {
        this.set({ step: "settled", order: current, intent: null });
        return;
      }
      const now = this.deps.now();
      if (
        opts.checkUntil !== undefined &&
        now >= opts.checkUntil &&
        canPayNow(current, now) &&
        current.paymentStatus !== "processing" &&
        current.paymentStatus !== "succeeded"
      ) {
        // Still plainly unpaid: offer the card form again (the same PaymentIntent can succeed at most once).
        if (this.s.intent && this.s.intent.orderId === current.orderId) {
          this.set({ step: "payment", cardMessage: opts.message ?? "The payment did not go through. You can try again before the hold ends." });
          return;
        }
        await this.preparePayment(token);
        return;
      }
      if (now - startedAt >= limit) {
        this.set({ pollTimedOut: true });
        return;
      }
    }
  }
}
